/* ============================================================================
   muc-track.js · Mi Universo Cuántico · medición de eventos propios (home + PDP)
   Origen: web-src/pdp/pdp2.js L11-23 y web-src/pdp/MEDICION.md (29 eventos, píxel personalizado «MUC · eventos PDP»).
   Un IIFE sin dependencias; cuelga de window.MUC.track (que además es una función). Cárgalo antes que el JS de la sección.

   API
     MUC.track(evento, datos)     -> datos publicados (objeto)
        evento: nombre SIN prefijo, p. ej. 'select_formato', 'form_error', 'add_to_cart' (lista: MEDICION.md §2).
        datos : objeto plano de parámetros (producto, formato, tamano, precio, items[]…), nunca datos de nacimiento.
        Hace tres cosas, todas a prueba de fallos (si algo no existe, no rompe la página):
          1. Shopify.analytics.publish('muc:' + evento, datos)  -> el píxel personalizado lo recibe en e.customData
             y lo reenvía a GA4 (MEDICION.md §4). Doc: shopify.dev/docs/api/web-pixels-api/emitting-data
          2. window.dataLayer.push({event: evento, ...datos})    -> por si algún día se usa GTM
          3. Con ?eventos=1 en la URL, escribe el evento en un panel flotante (Alt+E lo oculta/muestra). Sin ese
             parámetro el panel no existe. El panel marca en rojo lo que el filtro de privacidad ha quitado.
        Nota: ya no se llama a gtag() directamente (el prototipo lo hacía): el píxel reenvía a GA4 y así no se cuenta doble.
     MUC.track.base(obj)          -> fija parámetros por defecto de todos los eventos de la página (p. ej. {producto:'carta-natal'})
                                     y devuelve el conjunto actual. Se mezclan con los datos de cada llamada (gana la llamada).
     MUC.track.limpiar(obj)       -> copia de obj sin lo que no puede viajar (misma función que usa MUC.track; sirve para pruebas)
   Compatibilidad: window.MUC_track === MUC.track (nombre del prototipo).

   PRIVACIDAD (MEDICION.md §1): los eventos nunca llevan nombre, fecha, hora ni lugar de nacimiento. El módulo lo impone:
     - Quita las claves: nombre, name, apellido(s), fecha*, hora*, lugar*, ciudad, lat, lon, lng, latitud, longitud, tz,
       email, correo, telefono, phone, direccion, nacimiento, propiedades, properties, props, consentimiento, y toda clave
       que empiece por «_» (las propiedades privadas del pedido). («item_name» y «pais» sí pasan: pais = código ISO del país
       elegido, como define MEDICION.md para lugar_elegido.)
     - Quita cualquier valor de texto que parezca una fecha (dd/mm/aaaa, aaaa-mm-dd) o un email, y recorta textos a 120 caracteres.
     - Recorre objetos y listas hasta 3 niveles (items[] de GA4).
     Lo que el módulo NO puede saber: texto libre que escribe la persona. No pases como parámetro el texto libre del vínculo cuando
     rol = 'otro', ni el nombre de la pieza.
   ============================================================================ */
(() => {
  'use strict';
  const MUC = (window.MUC = window.MUC || {});
  const base = {};
  const CLAVE = /^(nombre|name|apellidos?|fecha|hora|lugar|ciudad|lat|lon|lng|latitud|longitud|tz|email|correo|telefono|phone|direccion|nacimiento|propiedades|properties|props|consentimiento)(_|$)|^_/i;
  const FECHA = /\b\d{1,2}[\/.-]\d{1,2}[\/.-]\d{4}\b|\b\d{4}-\d{2}-\d{2}\b/, EMAIL = /\S+@\S+\.\S+/;

  function limpiar(o, quitadas, nivel){
    nivel = nivel || 0; quitadas = quitadas || [];
    if (Array.isArray(o)) return nivel > 2 ? [] : o.map(v => (v && typeof v === 'object') ? limpiar(v, quitadas, nivel + 1) : v).filter(v => v !== undefined);
    const r = {};
    Object.keys(o || {}).forEach(k => {
      const v = o[k];
      if (CLAVE.test(k)){ quitadas.push(k); return; }
      if (typeof v === 'function' || v === undefined) return;
      if (typeof v === 'string'){ if (FECHA.test(v) || EMAIL.test(v)){ quitadas.push(k); return; } r[k] = v.length > 120 ? v.slice(0, 120) : v; }
      else if (v && typeof v === 'object'){ if (nivel > 2) return; r[k] = limpiar(v, quitadas, nivel + 1); }
      else r[k] = v;
    });
    return r;
  }

  /* panel de depuración: solo existe con ?eventos=1 */
  let panel = null, lista = null;
  const activo = () => { try { return new URLSearchParams(location.search).get('eventos') === '1'; } catch (e) { return false; } };
  function crearPanel(){
    if (panel || !document.body) return;
    panel = document.createElement('div'); panel.setAttribute('role', 'log'); panel.setAttribute('aria-label', 'Eventos de medición');
    panel.style.cssText = 'position:fixed;right:8px;bottom:8px;width:min(360px,92vw);max-height:40vh;overflow:auto;z-index:2147483000;background:#0B0B0B;color:#fff;font:11px/1.45 ui-monospace,Menlo,Consolas,monospace;padding:8px 10px;border:1px solid #8C8C8C;border-radius:6px';
    const cab = document.createElement('div'); cab.style.cssText = 'display:flex;justify-content:space-between;gap:8px;margin-bottom:4px;color:#8C8C8C';
    const t = document.createElement('span'); t.textContent = 'muc:* · Alt+E oculta';
    const x = document.createElement('button'); x.type = 'button'; x.textContent = '×'; x.setAttribute('aria-label', 'Cerrar el panel de eventos'); x.style.cssText = 'all:unset;cursor:pointer;color:#fff;padding:0 4px';
    x.addEventListener('click', () => { panel.hidden = true; });
    cab.append(t, x); lista = document.createElement('div'); panel.append(cab, lista); document.body.appendChild(panel);
  }
  if (activo()){
    if (document.body) crearPanel(); else document.addEventListener('DOMContentLoaded', crearPanel);
    addEventListener('keydown', e => { if (e.altKey && String(e.key).toLowerCase() === 'e' && panel) panel.hidden = !panel.hidden; });
  }
  function pintarPanel(ev, datos, quitadas){
    if (!activo()) return; crearPanel(); if (!lista) return;
    const d = document.createElement('div'), b = document.createElement('b'); b.textContent = ev;
    d.append(b, ' ' + JSON.stringify(datos));
    if (quitadas.length){ const q = document.createElement('span'); q.style.color = '#ff7b72'; q.textContent = ' · filtrado: ' + quitadas.join(', '); d.appendChild(q); }
    lista.prepend(d);
  }

  function track(ev, p){
    const quitadas = [], datos = Object.assign({}, limpiar(base, quitadas), limpiar(p || {}, quitadas));
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({event: ev}, datos)); } catch (e) {}
    try { const an = window.Shopify && window.Shopify.analytics; an && typeof an.publish === 'function' && an.publish('muc:' + ev, datos); } catch (e) {}
    try { pintarPanel(ev, datos, quitadas); } catch (e) {}
    return datos;
  }
  track.base = o => { if (o) Object.assign(base, o); return Object.assign({}, base); };
  track.limpiar = o => limpiar(o);
  MUC.track = track;
  window.MUC_track = track;
})();
